// components/CreatorMainMenu.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { API_URL } from '../reusable/url';
import './creatorMainMenu.css';

const CreatorMainMenu = () => {
  const [admins, setAdmins] = useState([]);
  const [showAdmins, setShowAdmins] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmAdmin, setConfirmAdmin] = useState(null); // { id, username } | null

  const authToken = useSelector((state) => state.user.token);

  const loadAdminsFromServer = () => {
    if (!authToken) {
      toast.error('No auth token found – please log in again.');
      return;
    }

    setLoading(true);

    fetch(API_URL('list-admins'), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'include',
    })
      .then(res => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then(data => {
            throw new Error(data.message || `Failed to load admins (status ${res.status})`);
          });
        }
        return res.json();
      })
      .then(data => {
        setAdmins(data);
        setShowAdmins(true);
        if (data.length === 0) {
          toast.info('No administrator accounts found.');
        }
      })
      .catch(err => {
        console.error(err);
        toast.error(err.message || 'Failed to load administrator accounts');
        setShowAdmins(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleToggleAdmins = (e) => {
    e.preventDefault();

    if (showAdmins) {
      setShowAdmins(false);
      return;
    }

    loadAdminsFromServer();
  };

  const deleteAdminOnServer = (adminId, username) => {
    if (!authToken) {
      toast.error('No auth token found – please log in again.');
      return;
    }

    fetch(API_URL(`delete-admin/${adminId}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      credentials: 'include',
    })
      .then(res => {
        return res.json().catch(() => ({})).then(data => ({ res, data }));
      })
      .then(({ res, data }) => {
        if (!res.ok) {
          throw new Error(data.message || `Failed to delete administrator (status ${res.status})`);
        }

        setAdmins(prev => prev.filter(a => a._id !== adminId));
        toast.success(`Administrator "${username}" deleted successfully.`);
      })
      .catch(err => {
        console.error(err);
        toast.error(err.message || 'Failed to delete administrator');
      });
  };

  const handleDeleteClick = (adminId, username) => {
    setConfirmAdmin({ id: adminId, username });
  };

  const handleConfirmDelete = () => {
    if (!confirmAdmin) return;
    deleteAdminOnServer(confirmAdmin.id, confirmAdmin.username);
    setConfirmAdmin(null);
  };

  const handleCancelDelete = () => {
    setConfirmAdmin(null);
  };

  return (
    <div className="creatorMainMenu-container">
      <h1>Creator Main Menu</h1>

      <div>
        <Link to="/generate-admin-token">
          <button className="btn-generate-admin-token">
            Generate Admin Token
          </button>
        </Link>
      </div>

      <div>
        <button
          className="btn-view-admins"
          type="button"
          onClick={handleToggleAdmins}
          disabled={loading}
        >
          {loading
            ? 'Loading administrator accounts…'
            : showAdmins
              ? 'Hide administrator accounts'
              : 'View administrator accounts'}
        </button>
      </div>

      {showAdmins && (
        <div className="admin-list-container">
          <h2>Administrator accounts</h2>
          {admins.length === 0 ? (
            <p className="creator-info">No administrator accounts found.</p>
          ) : (
            <ul className="admin-list">
              {admins.map(admin => (
                <li key={admin._id} className="admin-list-item">
                  <span className="admin-name">{admin.username}</span>
                  <button
                    type="button"
                    className="btn-delete-admin"
                    onClick={() => handleDeleteClick(admin._id, admin.username)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirmAdmin && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Delete administrator</h3>
            <p>
              Are you sure you want to delete administrator{' '}
              <strong>{confirmAdmin.username}</strong>?<br />
              This action cannot be undone.
            </p>
            <div className="modal-buttons">
              <button
                type="button"
                className="btn-confirm-delete"
                onClick={handleConfirmDelete}
              >
                Yes, delete
              </button>
              <button
                type="button"
                className="btn-cancel-delete"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorMainMenu;
